import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Plus, Pencil } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { downloadCsv } from "@/lib/utils";
import { Modal, Field, TextArea, SelectField, AdminButton } from "@/pages/admin/ui";
import { FILM_STATUS } from "@/lib/expeditions";

const fmt = (d) => new Date(d).toLocaleString("en-IN");
const selCls = "border border-ptf-primary/20 bg-white px-2 py-1.5 text-xs outline-none focus:border-ptf-secondary text-ptf-text";

function FilmsSection() {
  const qc = useQueryClient();
  const { data: films = [] } = useQuery({ queryKey: ["films"], queryFn: async () => (await api.get("/films")).data });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inv = () => qc.invalidateQueries({ queryKey: ["films"] });
  const save = useMutation({
    mutationFn: (b) => (editing?.id ? api.put(`/admin/films/${editing.id}`, b) : api.post("/admin/films", b)),
    onSuccess: () => { inv(); toast.success("Saved"); setEditing(null); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({ mutationFn: (id) => api.delete(`/admin/films/${id}`), onSuccess: () => { inv(); toast.success("Deleted"); } });
  const openEdit = (f) => {
    setEditing(f || {});
    setForm(f ? { ...f, filmmakers_s: (f.filmmakers || []).join("\n"), festivals_s: (f.festival_entries || []).map((e) => `${e.festival}|${e.country || ""}|${e.status || "SUBMITTED"}|${e.category || ""}`).join("\n") }
      : { title: "", tagline: "", year: "", location: "", duration: "", category: "DOCUMENTARIES", expedition_title: "", synopsis: "", story: "", behind: "", video_url: "", image: "", status: "PRE-PRODUCTION", filmmakers_s: "", festivals_s: "" });
  };
  const submit = (e) => {
    e.preventDefault();
    save.mutate({
      ...form,
      filmmakers: (form.filmmakers_s || "").split("\n").map((x) => x.trim()).filter(Boolean),
      festival_entries: (form.festivals_s || "").split("\n").map((x) => x.trim()).filter(Boolean).map((line) => {
        const [festival, country, status, category] = line.split("|").map((s) => (s || "").trim());
        return { festival, country, status: status || "SUBMITTED", category };
      }),
    });
  };
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">Films ({films.length})</h2>
        <AdminButton data-testid="add-film-btn" onClick={() => openEdit(null)}><Plus className="h-3.5 w-3.5" /> New Film</AdminButton>
      </div>
      <div className="space-y-2">
        {films.map((f) => (
          <div key={f.id} data-testid={`admin-film-${f.slug}`} className="border border-ptf-primary/10 bg-white px-4 py-3 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <p className="font-medium text-ptf-primary text-sm">{f.title}</p>
              <p className="text-[0.6rem] uppercase tracking-[0.12em] text-ptf-text/50">{f.category} · {FILM_STATUS[f.status] || f.status}{f.festival_entries?.length ? ` · ${f.festival_entries.length} festival entries` : ""}</p>
            </div>
            <button onClick={() => openEdit(f)} data-testid={`edit-film-${f.slug}`} className="grid place-items-center h-8 w-8 border border-ptf-primary/15 hover:bg-ptf-primary/5"><Pencil className="h-3.5 w-3.5 text-ptf-primary" /></button>
            <button onClick={() => { if (window.confirm("Delete this film?")) del.mutate(f.id); }} data-testid={`delete-film-${f.slug}`} className="grid place-items-center h-8 w-8 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
          </div>
        ))}
      </div>
      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? "Edit film" : "New film"}>
        {form && (
          <form onSubmit={submit} className="space-y-4" data-testid="film-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Title *" required value={form.title} onChange={set("title")} data-testid="ff-title" />
              <Field label="Tagline" value={form.tagline} onChange={set("tagline")} data-testid="ff-tagline" />
              <Field label="Year" value={form.year} onChange={set("year")} data-testid="ff-year" />
              <Field label="Location" value={form.location} onChange={set("location")} data-testid="ff-location" />
              <Field label="Duration" value={form.duration} onChange={set("duration")} data-testid="ff-duration" />
              <SelectField label="Category" value={form.category} onChange={set("category")} options={["DOCUMENTARIES", "SHORT FILMS", "EXPEDITION FILMS", "WILDLIFE", "ADVENTURE", "COMMUNITIES", "ENVIRONMENT", "FIELD STORIES"]} data-testid="ff-category" />
              <SelectField label="Production status" value={form.status} onChange={set("status")} options={Object.keys(FILM_STATUS)} data-testid="ff-status" />
              <Field label="Expedition title" value={form.expedition_title} onChange={set("expedition_title")} data-testid="ff-expedition" />
              <Field label="Video URL (YouTube)" value={form.video_url} onChange={set("video_url")} data-testid="ff-video" />
              <Field label="Image URL" value={form.image} onChange={set("image")} data-testid="ff-image" />
            </div>
            <TextArea label="Synopsis" value={form.synopsis} onChange={set("synopsis")} data-testid="ff-synopsis" />
            <TextArea label="The story" value={form.story} onChange={set("story")} data-testid="ff-story" />
            <TextArea label="Behind the journey" value={form.behind} onChange={set("behind")} data-testid="ff-behind" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextArea label="Filmmakers (one per line)" value={form.filmmakers_s} onChange={set("filmmakers_s")} data-testid="ff-filmmakers" />
              <TextArea label="Festival entries (Festival|Country|STATUS|Category per line)" value={form.festivals_s} onChange={set("festivals_s")} data-testid="ff-festivals" />
            </div>
            <AdminButton type="submit" data-testid="ff-save">Save Film</AdminButton>
          </form>
        )}
      </Modal>
    </div>
  );
}

function ApplicationsSection() {
  const qc = useQueryClient();
  const { data: apps = [] } = useQuery({ queryKey: ["exp-apps"], queryFn: async () => (await api.get("/admin/expedition-applications")).data });
  const patch = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/admin/expedition-applications/${id}/status`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["exp-apps"] }); toast.success("Status updated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({ mutationFn: (id) => api.delete(`/admin/expedition-applications/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["exp-apps"] }); toast.success("Deleted"); } });
  const exportCsv = () => downloadCsv(
    [["expedition", "role", "name", "email", "phone", "country", "portfolio", "status", "created_at"],
      ...apps.map((a) => [a.journey_title, a.role, a.name, a.email, a.phone, a.country, a.portfolio_url, a.status, a.created_at])],
    "expedition-applications.csv"
  );
  return (
    <div className="mt-14">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">Expedition Applications ({apps.length})</h2>
        {apps.length > 0 && <AdminButton variant="outline" data-testid="export-exp-apps-csv" onClick={exportCsv}>CSV</AdminButton>}
      </div>
      <div className="space-y-2">
        {apps.length === 0 && <p className="text-sm text-ptf-text/50">No applications yet.</p>}
        {apps.map((a) => (
          <div key={a.id} data-testid="exp-app-row" className="border border-ptf-primary/10 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium text-ptf-primary text-sm">{a.name} · {a.role} · {a.journey_title}</p>
              <span className="flex items-center gap-2">
                <select value={a.status} onChange={(e) => patch.mutate({ id: a.id, status: e.target.value })} data-testid="exp-app-status-select" className={selCls}>
                  {["received", "shortlisted", "accepted", "declined"].map((s) => <option key={s}>{s}</option>)}
                </select>
                <span className="text-xs text-ptf-text/45">{fmt(a.created_at)}</span>
                <button onClick={() => { if (window.confirm("Delete this application?")) del.mutate(a.id); }} data-testid="delete-exp-app" className="grid place-items-center h-7 w-7 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
              </span>
            </div>
            <p className="text-xs text-ptf-secondary mt-1">{a.email}{a.phone ? ` · ${a.phone}` : ""}{a.portfolio_url ? ` · ${a.portfolio_url}` : ""}</p>
            {a.motivation && <p className="text-xs text-ptf-text/70 mt-1.5">{a.motivation}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function JournalsSection() {
  const qc = useQueryClient();
  const { data: journeys = [] } = useQuery({ queryKey: ["journeys"], queryFn: async () => (await api.get("/journeys")).data });
  const [journeyId, setJourneyId] = useState("");
  const activeId = journeyId || journeys[0]?.id || "";
  const { data: entries = [] } = useQuery({
    queryKey: ["admin-journal", activeId],
    queryFn: async () => (await api.get(`/journals?journey_id=${activeId}`)).data,
    enabled: !!activeId,
  });
  const [form, setForm] = useState({ day: "", date: "", location: "", note: "", image: "", video_url: "", next_objective: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const create = useMutation({
    mutationFn: (b) => api.post("/admin/journals", { ...b, journey_id: activeId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-journal", activeId] }); toast.success("Field note published"); setForm({ day: "", date: "", location: "", note: "", image: "", video_url: "", next_objective: "" }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({ mutationFn: (id) => api.delete(`/admin/journals/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-journal", activeId] }); toast.success("Deleted"); } });
  return (
    <div className="mt-14">
      <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary mb-5">Field Journals</h2>
      <select value={activeId} onChange={(e) => setJourneyId(e.target.value)} data-testid="journal-journey-select" className={`${selCls} mb-4`}>
        {journeys.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
      </select>
      <form onSubmit={(e) => { e.preventDefault(); create.mutate(form); }} className="border border-ptf-primary/12 bg-white p-5 grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5" data-testid="journal-form">
        <Field label="Day (e.g. 04)" value={form.day} onChange={set("day")} data-testid="jf-day" />
        <Field label="Date" value={form.date} onChange={set("date")} data-testid="jf-date" />
        <Field label="Location" value={form.location} onChange={set("location")} data-testid="jf-location" />
        <div className="sm:col-span-3"><TextArea label="Field note *" required value={form.note} onChange={set("note")} data-testid="jf-note" /></div>
        <Field label="Image URL" value={form.image} onChange={set("image")} data-testid="jf-image" />
        <Field label="Video URL" value={form.video_url} onChange={set("video_url")} data-testid="jf-video" />
        <Field label="Next field objective" value={form.next_objective} onChange={set("next_objective")} data-testid="jf-next" />
        <div className="sm:col-span-3"><AdminButton type="submit" data-testid="jf-publish">Publish Field Note</AdminButton></div>
      </form>
      <div className="space-y-2">
        {entries.map((e) => (
          <div key={e.id} data-testid="admin-journal-row" className="border border-ptf-primary/10 bg-white p-4 flex items-start justify-between gap-3">
            <div>
              <p className="text-[0.62rem] uppercase tracking-[0.12em] text-ptf-text/50">{[e.day && `Day ${e.day}`, e.date, e.location].filter(Boolean).join(" · ")}</p>
              <p className="text-sm text-ptf-text/75 mt-1">{e.note}</p>
            </div>
            <button onClick={() => { if (window.confirm("Delete this field note?")) del.mutate(e.id); }} data-testid="delete-journal-entry" className="grid place-items-center h-7 w-7 border border-ptf-primary/15 hover:bg-red-50 shrink-0"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ExpeditionsAdmin() {
  return (
    <div>
      <FilmsSection />
      <ApplicationsSection />
      <JournalsSection />
    </div>
  );
}
